import { sendAnalytics } from '@audio-network/analytics'
import {
	Card,
	Container,
	Grid,
	GridItem,
	PlaceholderAlbumArtwork,
	Select,
	Text,
} from '@audio-network/componentlibrary'
import { usePlayer } from '@audio-network/player'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import React from 'react'

import optimizeContentfulImage from '@/utils/contentful/optimizeContentfulImage'
import formatDate from '@/utils/formatting/formatDate'
import { formatResultsCount } from '@/utils/formatting/formatResultsCount'
import isDateWithinFortnight from '@/utils/isDateWithinFortnight'
import { getAlbumUrl } from '@/utils/urls'

import styles from './AlbumList.module.css'
import { AlbumListProps } from './AlbumList.types'

// NOTE: The albums page does not use the CardList component and has bigger images
const ALBUM_ARTWORK_MAX_WIDTH = 600

const AlbumList = ({
	albums,
	albumCount,
	genres,
	defaultGenre,
	locale,
	onSelect,
	albumPreviewTracks,
	...props
}: AlbumListProps): JSX.Element => {
	const {
		togglePlay,
		isPlaying: isPlayingFromPlayer,
		currentTrack,
	} = usePlayer()
	const { t } = useTranslation(['albums'])
	const lastUpdated = formatDate(albums[0].releaseDate, locale, 'long')

	const ALL = 'all'

	const handleAlbumClick = (album: Album, index: number): void => {
		sendAnalytics({
			noun: 'Album',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				AlbumNo: album.number,
				AlbumTitle: album.name,
				Position: index + 1,
			},
		})
	}

	return (
		<section {...props}>
			<Container maxWidth="large">
				<div className={styles.albumListHeader}>
					<div className={styles.albumListDetails}>
						<span className={styles.lastUpdated} data-testid="last-updated">
							{t('updated')}{' '}
							<span className={styles.lastUpdatedDate}>{lastUpdated}</span>
						</span>

						<span className={styles.albumCount} data-testid="album-count">
							{formatResultsCount(albumCount, locale)} {t('albums')}
						</span>
					</div>
					<Text>{t('sorted-by')}</Text>
					{genres?.length > 0 && (
						<form noValidate>
							<Select
								aria-label={t('filter-by-genre')}
								value={defaultGenre ?? ALL}
								id="genres"
								name="genres"
								onInput={onSelect}
								options={[
									{
										label: t('all-albums'),
										value: ALL,
									},
									...genres.map((genre) => ({
										label: genre.name,
										value: genre.id,
									})),
								]}
							/>
						</form>
					)}
				</div>
				<Grid>
					{albums?.map((album, index) => {
						const albumURL = getAlbumUrl(album)
						const albumPreviewTrack = albumPreviewTracks.find(
							(track) => track.album.number === album.number
						)
						return (
							<GridItem key={album.number} mobile={2} tablet={2} desktop={2}>
								<Card
									title={album.name}
									flag={
										isDateWithinFortnight(album.releaseDate) ? t('new') : null
									}
									description={
										album.shortDescription ??
										album.longDescription ??
										// Fall back to Music API description
										album.description
									}
									href={albumURL}
									onClick={() => {
										handleAlbumClick(album, index)
									}}
									togglePlay={
										albumPreviewTrack &&
										(() => togglePlay(albumPreviewTrack, { source: 'Albums' }))
									}
									isPlaying={
										isPlayingFromPlayer &&
										albumPreviewTrack?.id === currentTrack.id
									}
									buttonProps={{
										'aria-label':
											isPlayingFromPlayer &&
											albumPreviewTrack?.id === currentTrack.id
												? `pause ${album.name}`
												: `play ${album.name}`,
									}}
									thumbnail={
										album.artworkImageUrl ? (
											<Image
												alt=""
												src={optimizeContentfulImage(album.artworkImageUrl, {
													height: ALBUM_ARTWORK_MAX_WIDTH,
													width: ALBUM_ARTWORK_MAX_WIDTH,
												})}
												height={ALBUM_ARTWORK_MAX_WIDTH}
												width={ALBUM_ARTWORK_MAX_WIDTH}
											/>
										) : (
											<PlaceholderAlbumArtwork
												height={600}
												width={600}
												className={styles.placeholderArtwork}
											/>
										)
									}
								/>
							</GridItem>
						)
					})}
				</Grid>
			</Container>
		</section>
	)
}

export default AlbumList
